import type * as React from "react";
import { useMemo, useState } from "react";
import { getAnimals, setAnimals } from "../../../../store/animalsStore";
import type { AnimalItem } from "../../../../mocks/animals";
import BackdropCloseContainer from "../../../../components/ui/BackdropCloseContainer";

interface Props {
  onClose: () => void;
}

export default function AddAnimalModal({ onClose }: Props) {
  const existingAnimals = useMemo(() => getAnimals(), []);

  const nextId = useMemo(() => {
    return existingAnimals.length > 0
      ? Math.max(...existingAnimals.map((a) => a.id)) + 1
      : 1;
  }, [existingAnimals]);

  const nextSiraNo = useMemo(() => {
    return existingAnimals.length > 0
      ? Math.max(...existingAnimals.map((a) => a.siraNo)) + 1
      : 1;
  }, [existingAnimals]);

  const [form, setForm] = useState({
    siraNo: String(nextSiraNo),
    kupeNo: "",
    tur: "Büyükbaş" as AnimalItem["tur"],
    kg: "",
    referansFiyat: "",
    toplamHisse: "1",
    durum: "Aktif" as AnimalItem["durum"],
    kesimGunu: "",
    hisseliSatisEtiketi: false,
    disKesim: false,
    defaultKesimUcreti: "",
    defaultParcalamaUcreti: "",
    defaultTasimaUcreti: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;

    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      setForm((prev) => ({ ...prev, [target.name]: target.checked }));
      return;
    }

    setForm((prev) => ({ ...prev, [target.name]: target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newAnimal: AnimalItem = {
      id: nextId,
      siraNo: Number(form.siraNo),
      kupeNo: form.kupeNo.trim(),
      tur: form.tur,
      kg: form.kg ? Number(form.kg) : undefined,
      referansFiyat: form.referansFiyat
        ? Number(form.referansFiyat)
        : undefined,
      toplamHisse: Number(form.toplamHisse),
      satirlanHisse: 0,
      toplamSatisTutari: 0,
      toplamOdenen: 0,
      durum: form.durum,
      kesimeUygun: false,
      tamamlandi: false,
      kesimGunu: form.kesimGunu || undefined,
      hisseliSatisEtiketi: form.hisseliSatisEtiketi,
      disKesim: form.disKesim,
      defaultKesimUcreti: form.defaultKesimUcreti
        ? Number(form.defaultKesimUcreti)
        : undefined,
      defaultParcalamaUcreti: form.defaultParcalamaUcreti
        ? Number(form.defaultParcalamaUcreti)
        : undefined,
      defaultTasimaUcreti: form.defaultTasimaUcreti
        ? Number(form.defaultTasimaUcreti)
        : undefined,
      hisseler: [],
    };

    void setAnimals((prev) => [...prev, newAnimal]);
    onClose();
  };

  return (
    <BackdropCloseContainer
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm px-0 sm:px-4"
    >
      <div className="w-full sm:max-w-sm bg-farm-warm border border-farm-border rounded-t-farm sm:rounded-farm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center text-farm-earth">
              <i className="ri-add-circle-line text-base" />
            </div>
            <h2 className="font-heading font-semibold text-sm text-farm-brown">
              Yeni Hayvan Ekle
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-farm-muted hover:text-farm-brown rounded-full hover:bg-farm-cream transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-4 py-4 flex flex-col gap-3 max-h-[80vh] overflow-y-auto"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="siraNo">
                Küpe No
              </label>
              <input
                id="siraNo"
                name="siraNo"
                type="number"
                value={form.siraNo}
                onChange={handleChange}
                placeholder="13"
                required
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="tur">
                Hayvan Türü
              </label>
              <select
                id="tur"
                name="tur"
                value={form.tur}
                onChange={handleChange}
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors cursor-pointer"
              >
                <option value="Büyükbaş">Büyükbaş</option>
                <option value="Küçükbaş">Küçükbaş</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="kupeNo">
              Sipariş No
            </label>
            <input
              id="kupeNo"
              name="kupeNo"
              type="text"
              value={form.kupeNo}
              onChange={handleChange}
              placeholder="TR-4829-2024"
              required
              className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="kg">
                Ağırlık (kg)
              </label>
              <input
                id="kg"
                name="kg"
                type="number"
                min="0"
                value={form.kg}
                onChange={handleChange}
                placeholder="390"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="referansFiyat">
                Ref. Fiyat (₺/kg)
              </label>
              <input
                id="referansFiyat"
                name="referansFiyat"
                type="number"
                min="0"
                value={form.referansFiyat}
                onChange={handleChange}
                placeholder="155"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="toplamHisse">
                Toplam Hisse
              </label>
              <input
                id="toplamHisse"
                name="toplamHisse"
                type="number"
                min="1"
                max="14"
                value={form.toplamHisse}
                onChange={handleChange}
                required
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="durum">
                Durum
              </label>
              <select
                id="durum"
                name="durum"
                value={form.durum}
                onChange={handleChange}
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors cursor-pointer"
              >
                <option value="Aktif">Aktif</option>
                <option value="Kesim Bekliyor">Kesim Bekliyor</option>
                <option value="Kesildi">Kesildi</option>
                <option value="Teslim Edildi">Teslim Edildi</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="kesimGunu">
                Kesim Günü
              </label>
              <input
                id="kesimGunu"
                name="kesimGunu"
                type="date"
                value={form.kesimGunu}
                onChange={handleChange}
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-farm-border bg-farm-cream cursor-pointer">
                <input
                  id="hisseliSatisEtiketi"
                  name="hisseliSatisEtiketi"
                  type="checkbox"
                  checked={form.hisseliSatisEtiketi}
                  onChange={handleChange}
                  className="accent-[#8b5e3c]"
                />
                <span className="font-body text-xs font-semibold text-farm-brown">
                  Hisseli Satış
                </span>
              </label>

              <label className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-farm-border bg-farm-cream cursor-pointer">
                <input
                  id="disKesim"
                  name="disKesim"
                  type="checkbox"
                  checked={form.disKesim}
                  onChange={handleChange}
                  className="accent-[#8b5e3c]"
                />
                <span className="font-body text-xs font-semibold text-farm-brown">
                  Dış Kesim
                </span>
              </label>
            </div>
          </div>

          <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mt-1">
            Varsayılan Hizmet Ücretleri
          </p>

          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="defaultKesimUcreti">
                Kesim (₺)
              </label>
              <input
                id="defaultKesimUcreti"
                name="defaultKesimUcreti"
                type="number"
                min="0"
                value={form.defaultKesimUcreti}
                onChange={handleChange}
                placeholder="600"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="defaultParcalamaUcreti">
                Parçalama (₺)
              </label>
              <input
                id="defaultParcalamaUcreti"
                name="defaultParcalamaUcreti"
                type="number"
                min="0"
                value={form.defaultParcalamaUcreti}
                onChange={handleChange}
                placeholder="350"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="defaultTasimaUcreti">
                Taşıma (₺)
              </label>
              <input
                id="defaultTasimaUcreti"
                name="defaultTasimaUcreti"
                type="number"
                min="0"
                value={form.defaultTasimaUcreti}
                onChange={handleChange}
                placeholder="200"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div className="flex items-start gap-2 px-2.5 py-2 rounded-md bg-farm-gold-pale border border-farm-gold/20">
            <div className="w-4 h-4 flex items-center justify-center text-farm-gold flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-xs" />
            </div>
            <p className="font-body text-[10px] text-farm-muted leading-relaxed">
              Hisseli Satış ve Dış Kesim kutuları bilgi / filtre alanıdır. Matematiksel etkisi yoktur.
            </p>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-full border border-farm-border text-farm-muted font-body text-xs font-semibold hover:bg-farm-cream transition-colors whitespace-nowrap cursor-pointer"
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 rounded-full bg-farm-earth text-white font-body text-xs font-semibold hover:bg-farm-brown transition-colors whitespace-nowrap cursor-pointer"
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </BackdropCloseContainer>
  );
}
